
import React, { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Trash, Move, Plus } from 'lucide-react';
import { CertificateTemplateElement } from '@/types/certificateTemplate';

export interface PDFTemplateMappingField {
  id: string;
  name: string;
  dataField: string;
  position: {
    x: number;
    y: number;
    width: number;
    height: number;
  };
}

export interface PDFTemplateMapperProps {
  pdfUrl: string;
  existingFields?: CertificateTemplateElement[];
  onSaveMapping: (fields: CertificateTemplateElement[]) => void;
}

const PDFTemplateMapper: React.FC<PDFTemplateMapperProps> = ({
  pdfUrl,
  existingFields = [],
  onSaveMapping
}) => {
  const pdfContainerRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);
  const [fields, setFields] = useState<CertificateTemplateElement[]>(existingFields);
  const [activeFieldIndex, setActiveFieldIndex] = useState<number | null>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [startPos, setStartPos] = useState({ x: 0, y: 0 });
  const [dataFields] = useState([
    'studentName',
    'courseName',
    'programTitle',
    'completionDate',
    'enrollmentDate',
    'grade',
    'instructorName',
    'teacherName',
    'trainingTitle',
    'verificationCode'
  ]);

  // Update scale on container resize
  useEffect(() => {
    const updateDimensions = () => {
      if (pdfContainerRef.current) {
        const { width } = pdfContainerRef.current.getBoundingClientRect();
        // Adjust scale based on container width (assuming PDF is 800px wide)
        setScale(width / 800);
      }
    };
    
    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    return () => window.removeEventListener('resize', updateDimensions);
  }, []);

  const handleMouseDown = (e: React.MouseEvent) => {
    if (activeFieldIndex !== null) return;
    
    const pdfContainer = pdfContainerRef.current;
    if (!pdfContainer) return;
    
    const rect = pdfContainer.getBoundingClientRect();
    const x = (e.clientX - rect.left) / scale;
    const y = (e.clientY - rect.top) / scale;
    
    setStartPos({ x, y });
    setIsDrawing(true);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDrawing || activeFieldIndex !== null) return;
    
    const pdfContainer = pdfContainerRef.current;
    if (!pdfContainer) return;
    
    const rect = pdfContainer.getBoundingClientRect();
    const currentX = (e.clientX - rect.left) / scale;
    const currentY = (e.clientY - rect.top) / scale;
    
    const width = Math.abs(currentX - startPos.x);
    const height = Math.abs(currentY - startPos.y);
    const x = Math.min(startPos.x, currentX);
    const y = Math.min(startPos.y, currentY);
    
    // Update the "drawing" field (last field)
    const updatedFields = [...fields];
    const drawingFieldIndex = updatedFields.length;
    
    if (drawingFieldIndex === fields.length) {
      // Add a new field if we don't have a temporary one
      updatedFields.push({
        id: `temp-${Date.now()}`,
        type: 'text',
        dynamic: true,
        dataField: '',
        position: {
          x: x + width / 2,
          y: y + height / 2
        },
        style: {
          width: `${width}px`,
          height: `${height}px`,
          fontSize: '16px',
          color: '#000000',
        }
      });
    } else {
      // Update the existing temporary field
      updatedFields[drawingFieldIndex] = {
        ...updatedFields[drawingFieldIndex],
        position: {
          x: x + width / 2,
          y: y + height / 2
        },
        style: {
          ...updatedFields[drawingFieldIndex].style,
          width: `${width}px`,
          height: `${height}px`,
        }
      };
    }
    
    setFields(updatedFields);
  };

  const handleMouseUp = () => {
    if (!isDrawing) return;
    setIsDrawing(false);
    
    // Set the active field to the one we just created
    setActiveFieldIndex(fields.length - 1);
  };

  const handleDragStart = (index: number, e: React.MouseEvent) => {
    e.preventDefault();
    setActiveFieldIndex(index);
  };

  const handleDragMove = (e: React.MouseEvent) => {
    if (activeFieldIndex === null) return;
    
    const pdfContainer = pdfContainerRef.current;
    if (!pdfContainer) return;
    
    const rect = pdfContainer.getBoundingClientRect();
    const x = (e.clientX - rect.left) / scale;
    const y = (e.clientY - rect.top) / scale;
    
    const updatedFields = [...fields];
    updatedFields[activeFieldIndex] = {
      ...updatedFields[activeFieldIndex],
      position: { x, y }
    };
    
    setFields(updatedFields);
  };

  const handleDragEnd = () => {
    setActiveFieldIndex(null);
  };

  const handleFieldChange = (index: number, dataField: string) => {
    const updatedFields = [...fields];
    updatedFields[index] = {
      ...updatedFields[index],
      dataField
    };
    setFields(updatedFields);
  };

  const handleRemoveField = (index: number) => {
    const updatedFields = [...fields];
    updatedFields.splice(index, 1);
    setFields(updatedFields);
  };

  const handleSave = () => {
    // Filter out any fields that don't have a dataField assigned
    const validFields = fields.filter(field => field.dataField);
    onSaveMapping(validFields);
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">Map Fields on PDF Template</h3>
        <div className="space-x-2">
          <Button
            variant="outline"
            onClick={() => setFields(existingFields)}
          >
            Reset
          </Button>
          <Button onClick={handleSave}>
            Save Mapping
          </Button>
        </div>
      </div>
      
      <Card>
        <CardContent className="p-4">
          <div 
            ref={pdfContainerRef}
            className="relative border rounded overflow-hidden h-[600px] cursor-crosshair"
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={() => {
              if (isDrawing) setIsDrawing(false);
              if (activeFieldIndex !== null) setActiveFieldIndex(null);
            }}
          >
            {/* PDF Background */}
            <iframe
              src={`${pdfUrl}#toolbar=0`}
              className="w-full h-full pointer-events-none"
              title="PDF Certificate Template"
            />
            
            {/* Mapped Fields */}
            {fields.map((field, index) => {
              // Calculate position and dimensions
              const width = parseInt(field.style?.width || '100');
              const height = parseInt(field.style?.height || '30');
              
              // Calculate top-left position (centered around the position point)
              const x = field.position.x - (width / 2);
              const y = field.position.y - (height / 2);
              
              return (
                <div 
                  key={field.id}
                  className={`absolute border-2 ${
                    activeFieldIndex === index ? 'border-primary bg-primary/10' : 'border-blue-500 bg-blue-100/30'
                  } flex items-center justify-center cursor-move`}
                  style={{
                    left: x * scale,
                    top: y * scale,
                    width: width * scale,
                    height: height * scale,
                  }}
                  onMouseDown={(e) => handleDragStart(index, e)}
                  onMouseMove={handleDragMove}
                  onMouseUp={handleDragEnd}
                >
                  <span className="text-xs font-medium px-1 select-none">
                    {field.dataField || 'Unassigned Field'}
                  </span>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
      
      <div>
        <h3 className="font-semibold mb-2">Field Mappings</h3>
        <div className="space-y-2">
          {fields.map((field, index) => (
            <div key={field.id} className="flex items-center gap-2 bg-muted p-2 rounded">
              <Move className="h-4 w-4 text-muted-foreground" />
              <div className="flex-1">
                <Select
                  value={field.dataField || ''}
                  onValueChange={(value) => handleFieldChange(index, value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select data field" />
                  </SelectTrigger>
                  <SelectContent>
                    {dataFields.map(dataField => (
                      <SelectItem key={dataField} value={dataField}>
                        {dataField}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => handleRemoveField(index)}
              >
                <Trash className="h-4 w-4" />
              </Button>
            </div>
          ))}
          
          <Button 
            variant="outline" 
            className="w-full mt-2"
            onClick={() => {
              const newField: CertificateTemplateElement = {
                id: `field-${Date.now()}`,
                type: 'text',
                dynamic: true,
                dataField: '',
                position: { x: 400, y: 300 },
                style: {
                  width: '150px',
                  height: '30px',
                  fontSize: '16px',
                  color: '#000000',
                }
              };
              setFields([...fields, newField]);
            }}
          >
            <Plus className="h-4 w-4 mr-2" /> Add Field
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PDFTemplateMapper;
